/**
 * Using potions in the world: drinking, splash application and bottle filling rules. DOM-free.
 *
 *   isDrinkable(id)                         potion bottle or milk bucket (hold right mouse 1.6 s)
 *   applyEffectList(set, target, list, k)   give a potion's effects to a player/mob view, scaled by k
 *   drinkStack(set, target, stack)          effects of drinking `stack` (milk clears everything)
 *   emptiedBy(id)                           item left behind (glass bottle / bucket) or 0
 */

import { I } from '../itemIds';
import { FX, applyInstant, effectDef, type EffectSet, type EffectTarget } from '../effects/effects';
import { drinkResult } from '../effects/sources';
import { potionEffects, type PotionEffect } from './potions';
import type { Stack } from '../types';

/** Vanilla drink time: 32 ticks (1.6 s). */
export const DRINK_TICKS = 32;

export const isDrinkable = (id: number): boolean => id === I.POTION || id === I.MILK_BUCKET;

/** Give `list` to a target. Instants act at once (scaled), the rest are stored with their duration scaled and rounded. */
export function applyEffectList(set: EffectSet, target: EffectTarget, list: readonly PotionEffect[], scale = 1): void {
  for (const e of list) {
    if (effectDef(e.id)?.instant) { applyInstant(e.id, e.amp, target, scale); continue; }
    if (target.undead && (e.id === FX.POISON || e.id === FX.REGENERATION)) continue;
    const dur = Math.floor(scale * e.dur + 0.5);
    if (dur > 0) set.add(e.id, e.amp, dur);
  }
}

export function drinkStack(set: EffectSet, target: EffectTarget, stack: Stack): void {
  if (stack.id === I.MILK_BUCKET) { set.clear(); return; }
  if (stack.id === I.POTION) applyEffectList(set, target, potionEffects(stack.id, stack.dmg));
}

export const emptiedBy = (id: number): number => (id === I.POTION ? I.GLASS_BOTTLE : drinkResult(id));
